import { useContext } from 'react';

import { PermissionsContext } from '../context/PermissionsProvider';

/**
 * The guard primitive: `{ allowed, loading, mode }` for a permission key. `mode` is the
 * per-call override, else `DEFAULT_UNAUTHORIZED_MODE`. While loading, `allowed` is false.
 *
 * @param {string} key A value of `PERMISSIONS`.
 * @param {{ mode?: 'hide'|'lock' }} [options]
 */
export const usePermission = (key, options) => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermission must be used inside a PermissionsProvider');

  return ctx.check(key, options);
};

/** `check(key, options)` for non-hook consumers (e.g. `filterMenuByPermission`). */
export const usePermissionCheck = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissionCheck must be used inside a PermissionsProvider');

  return ctx.check;
};

/** The signed-in actor (`{ user_type, permissions }`), or null while loading / on failure. */
export const useActor = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('useActor must be used inside a PermissionsProvider');
  return ctx.actor;
};

/**
 * The whole permissions state, for components that filter a list or decide per row:
 * `{ loading, check, actor, mode, noAccessRedirectPath }` (`mode` is the default hide/lock rule).
 */
export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used inside a PermissionsProvider');

  return ctx;
};
